import { baseApi as api } from "./base-api";

/**
 * API slice for privacy-policy and conditions related endpoints.
 *
 * These endpoints are NOT present in the autogenerated swagger file yet, so we
 * define them manually to keep the generator output intact. They utilise the
 * same baseApi instance and therefore share its baseQuery configuration
 * (including auth headers, error handling, etc.).
 */

// Types matching the backend data models
export enum ConditionType {
  Default = 0,  // Shared across courses
  Unique = 1    // Course-specific
}

export interface CourseCondition {
  id: number;
  courseId: number;
  privacyConditionType?: ConditionType;
  privacyConditionText?: string;
  userConditionType?: ConditionType;
  userConditionText?: string;
  version: number;
  isActive: boolean;
  showPrivacyConditions: boolean;
  showUserConditions: boolean;
  
  // New fields for separate controls
  privacyConditionVisible: boolean;
  privacyConditionForceReSign: boolean;
  userConditionVisible: boolean;
  userConditionForceReSign: boolean;
  currentVersion: number;
  signedVersion: number;
  makeUsersSignAgain: boolean;
  visibility: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConditionSignature {
  id: number;
  userId: number;
  courseConditionId: number;
  conditionVersion: number; // Version signed by user
  privacyConditionText: string; // Snapshot at time of signing
  userConditionText: string; // Snapshot at time of signing  
  signedAt: string;
}

export interface CourseConditionsResponse {
  exists: boolean;
  conditions?: CourseCondition;
}

// Updated response types to match new backend specification
export interface SignConditionsResponse {
  success: boolean;
  signatureId: number;
  signedAt: string;
  conditionType?: 'privacy' | 'user' | 'both';
  debug?: {
    userId: number;
    courseId: number;
    conditionId: number;
    signatureId: number;
    timestamp: string;
  };
}

// Enhanced status response with individual tracking
export interface UserSignatureStatusResponse {
  hasSigned: boolean;              // Both conditions signed
  hasSignedPrivacy: boolean;       // Privacy condition signed  
  hasSignedUser: boolean;          // User condition signed
  currentVersion: number;
  signedVersion: number;
  individual: {                    // Individual condition details
    privacy: {
      signed: boolean;
      visible: boolean;
      text: string | null;         // null if already signed, text if needs signing
    };
    user: {
      signed: boolean;
      visible: boolean;
      text: string | null;         // null if already signed, text if needs signing
    };
  };
  
  // Legacy fields for backward compatibility
  needsToSign?: boolean;
  showPrivacyConditions?: boolean;
  showUserConditions?: boolean;
  needsToSignPrivacy?: boolean;
  needsToSignUser?: boolean;
  privacySignedVersion?: number;
  userSignedVersion?: number;
}
const injectedPrivacyPolicyApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get course conditions for frontend users.
     * Endpoint example: GET /api/courses/123/conditions
     */
    getCourseConditions: build.query<CourseConditionsResponse, { courseId: number }>({
      query: ({ courseId }) => `/api/courses/${courseId}/conditions`,
    }),

    /**
     * Sign conditions for a specific course.
     * Endpoint example: POST /api/courses/123/conditions/sign
     */
    signCourseConditions: build.mutation<SignConditionsResponse, { courseId: number }>({
      query: ({ courseId }) => ({
        url: `/api/courses/${courseId}/conditions/sign`,
        method: "POST",
      }),
    }),

    /**
     * Sign privacy conditions only for a specific course.
     * Endpoint example: POST /api/courses/123/conditions/sign-privacy
     */
    signPrivacyConditions: build.mutation<SignConditionsResponse, { courseId: number }>({
      query: ({ courseId }) => ({
        url: `/api/courses/${courseId}/conditions/sign-privacy`,
        method: "POST",
      }),
    }),

    /**
     * Sign user conditions only for a specific course.
     * Endpoint example: POST /api/courses/123/conditions/sign-user
     */
    signUserConditions: build.mutation<SignConditionsResponse, { courseId: number }>({
      query: ({ courseId }) => ({
        url: `/api/courses/${courseId}/conditions/sign-user`,
        method: "POST",
      }),
    }),

    /**
     * Check if user has signed current version of conditions.
     * Endpoint example: GET /api/courses/123/conditions/signed
     */
    getUserSignatureStatus: build.query<UserSignatureStatusResponse, { courseId: number }>({
      query: ({ courseId }) => `/api/courses/${courseId}/conditions/signed`,
    }),

    /**
     * Get user's condition signature history (optional).
     * Endpoint example: GET /api/users/me/condition-signatures
     */
    getUserConditionSignatures: build.query<ConditionSignature[], void>({
      query: () => `/api/users/me/condition-signatures`,
    }),

    // Legacy privacy policy endpoints for backward compatibility
    /**
     * Fetch the privacy-policy text (HTML) for a specific course.
     * Endpoint example: GET /courses/123/privacy-policy
     */
    getCoursePrivacyPolicy: build.query<
      { policy: string; required: boolean },
      { courseId: number }
    >({
      query: ({ courseId }) => `/courses/${courseId}/privacy-policy`,
    }),

    /**
     * Check whether the current user already accepted the privacy-policy for a
     * specific course.
     * Endpoint example: GET /courses/123/privacy-policy/has-accepted
     */
    getCoursePolicyAccepted: build.query<
      { accepted: boolean },
      { courseId: number }
    >({
      query: ({ courseId }) =>
        `/courses/${courseId}/privacy-policy/accepted`,
    }),

    /**
     * Accept the privacy-policy for a specific course.
     * Endpoint example: POST /courses/123/privacy-policy/accept
     */
    acceptCoursePolicy: build.mutation<
      unknown,
      { courseId: number }
    >({
      query: ({ courseId }) => ({
        url: `/courses/${courseId}/privacy-policy/accept`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  // Enhanced conditions endpoints
  useGetCourseConditionsQuery,
  useSignCourseConditionsMutation,
  useSignPrivacyConditionsMutation,
  useSignUserConditionsMutation,
  useGetUserSignatureStatusQuery,
  useGetUserConditionSignaturesQuery,
  
  // Legacy privacy policy endpoints for backward compatibility
  useGetCoursePrivacyPolicyQuery,
  useGetCoursePolicyAcceptedQuery,
  useAcceptCoursePolicyMutation,
} = injectedPrivacyPolicyApi; 